import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import {
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Card,
  CardMedia,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  useGetTemplateDetailsQuery,
  useGetFabricDetailsTemplatesQuery,
  useGetTrimDetailsTemplatesQuery,
  useSubmitTemplateDetailsMutation,
  useUpdateTemplateDetailsMutation,
} from "../../../../../../services/api/production-order-template";
import { LoadingButton } from "@mui/lab";
import { useNotification } from "../../../../../../Contexts/GlobalNotificationContext";
import { useStyles } from "../../../../../../Theme/Style";

import { useGetTrimTypeQuery } from "../../../../../../services/api/trim-types";

// ⬇️ You can adjust these to your real label APIs
// e.g. `production-order-template` service or similar
import {
  useGetLabelTypesQuery,
  useGetLabelsTemplatesQuery,
} from "../../../../../../services/api/labels-templates";

export const ProductionOrderTemplateForm = ({
  title,
  titleSingle,
  mode,
  paramdata,
  handleSuccessDialog,
  handleErrorAlert,
}) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  const { data: fabricData } = useGetFabricDetailsTemplatesQuery();
  const { data: trimData } = useGetTrimDetailsTemplatesQuery();
  const { data: trimTypeData, isLoading: trimTypeLoading } = useGetTrimTypeQuery();

  // ⬇️ Label data (adjust shape to your API)
  const { data: labelTypesData } = useGetLabelTypesQuery();
  const { data: labelsData } = useGetLabelsTemplatesQuery();

  const [submitTemplateDetails] = useSubmitTemplateDetailsMutation();
  const [updateTemplateDetails] = useUpdateTemplateDetailsMutation();

  const [template_name, setTemplateName] = useState("");

  // Fabric rows: { item: object|null, fabricTypes: object[] }
  const [fabricsItems, setFabricItems] = useState([{ item: null, fabricTypes: [] }]);

  // Trim rows: { item: object|null, trim_type: object|null }
  const [trimItems, setTrimItems] = useState([{ item: null, trim_type: null }]);

  // Label rows: { label_type: object|null, labels: object[] }
  const [labelItems, setLabelItems] = useState([{ label_type: null, labels: [] }]);

  const { data: templateDetails } = useGetTemplateDetailsQuery(
    { id: paramdata?.id },
    { skip: !paramdata?.id }
  );

  const list = (src) =>
    Array.isArray(src?.data)
      ? src.data
      : Array.isArray(src?.items)
      ? src.items
      : Array.isArray(src)
      ? src
      : [];

  // memoize options
  const trimTypeOptions = useMemo(() => list(trimTypeData), [trimTypeData]);

  const labelTypeOptions = useMemo(() => list(labelTypesData), [labelTypesData]);
  const labelOptions = useMemo(() => list(labelsData), [labelsData]);

  useEffect(() => {
    if (!templateDetails) return;

    setTemplateName(templateDetails.name || "");

    const incomingFabrics =
      (templateDetails.fabrics || []).map((f) => ({
        item: f.item || null,
        fabricTypes: f.fabricTypes || f.fabric_types || [],
      })) || [];
    setFabricItems(incomingFabrics.length ? incomingFabrics : [{ item: null, fabricTypes: [] }]);

    const incomingTrims =
      (templateDetails.trims || []).map((t) => ({
        item: t.item || null,
        trim_type: t.trim_type || t.type || null,
      })) || [];
    setTrimItems(incomingTrims.length ? incomingTrims : [{ item: null, trim_type: null }]);

    // ⬇️ Group labels by label_type
    const labelsByType = {};
    (templateDetails.labels || []).forEach((l) => {
      const typeId = l.label_type?.id;
      if (!typeId) return;
      
      if (!labelsByType[typeId]) {
        labelsByType[typeId] = {
          label_type: l.label_type,
          labels: []
        };
      }
      if (l.label) {
        labelsByType[typeId].labels.push(l.label);
      }
    });
    
    const incomingLabels = Object.values(labelsByType);
    setLabelItems(
      incomingLabels.length ? incomingLabels : [{ label_type: null, labels: [] }]
    );
  }, [templateDetails]);

  const isEqualById = (o, v) => String(o?.id ?? "") === String(v?.id ?? "");

  // -------- Fabric handlers --------
  const addNewRowFabric = () =>
    setFabricItems((prev) => [...prev, { item: null, fabricTypes: [] }]);

  const handleChangeFabric = (_e, name, index, value) => {
    setFabricItems((prev) => {
      const next = [...prev];
      next[index][name] = value;
      return next;
    });
  };

  const deleteRowsFabric = (index) => {
    setFabricItems((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next.length ? next : [{ item: null, fabricTypes: [] }];
    });
  };

  // -------- Trim handlers --------
  const addNewRowTrim = () =>
    setTrimItems((p) => [...p, { item: null, trim_type: null }]);

  const handleChangeTrim = (_e, fieldName, index, value) => {
    setTrimItems((prev) => {
      const next = [...prev];
      next[index][fieldName] = value ?? null;
      return next;
    });
  };

  const deleteRowTrim = (index) => {
    setTrimItems((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next.length ? next : [{ item: null, trim_type: null }];
    });
  };

  // -------- Label handlers (Label Type -> Multiple Labels) --------
  const addNewRowLabel = () =>
    setLabelItems((prev) => [...prev, { label_type: null, labels: [] }]);

  const handleChangeLabelRow = (_e, fieldName, index, value) => {
    setLabelItems((prev) => {
      const next = [...prev];
      const row = { ...next[index] };

      if (fieldName === "label_type") {
        row.label_type = value ?? null;
        // reset labels when type changes
        row.labels = [];
      } else if (fieldName === "labels") {
        row.labels = value ?? [];
      }

      next[index] = row;
      return next;
    });
  };

  const deleteRowLabel = (index) => {
    setLabelItems((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next.length ? next : [{ label_type: null, labels: [] }];
    });
  };

  const handleTemplateSubmit = async () => {
    try {
      const fabricsValid = fabricsItems.every(
        (r) => r.item?.id && Array.isArray(r.fabricTypes) && r.fabricTypes.length > 0
      );
      if (!fabricsValid) {
        throw new Error("Please select Item and at least one Fabric Type for each row.");
      }

      const trimsValid = trimItems.every((t) => {
        if (!t.item?.id && !t.trim_type?.id) return true; // empty row OK
        if (t.item?.id && t.trim_type?.id) return true;
        return false;
      });
      if (!trimsValid) {
        throw new Error("Please select a Trim Type for each selected Trim Item.");
      }

      // Labels valid: if label_type is selected, must have at least one label
      const labelsValid = labelItems.every((row) => {
        const hasType = !!row.label_type?.id;
        const hasLabels = Array.isArray(row.labels) && row.labels.length > 0;
        if (!hasType && !hasLabels) return true; // empty row OK
        return hasType && hasLabels;
      });
      if (!labelsValid) {
        throw new Error("Please select at least one Label for each Label Type.");
      }

      const data = {
        name: template_name,
        fabrics: fabricsItems.map((r) => ({
          item: r.item.id,
          fabric_types: (r.fabricTypes || []).map((ft) => ft.id),
        })),
        trims: trimItems
          .filter((t) => !!t.item?.id)
          .map((t) => ({
            item: t.item.id,
            trim_type: t.trim_type?.id ?? null,
          })),
        // Group labels by label_type
        labels: labelItems
          .filter((row) => row.label_type?.id && row.labels.length > 0)
          .map((row) => ({
            label_type: row.label_type.id,
            labels: row.labels.map((label) => label.id),
          })),
      };

      if (mode === "edit" && paramdata?.id) {
        await updateTemplateDetails({ id: paramdata.id, data }).unwrap();
        addNotification({ message: "Template details updated successfully!", type: "success" });
      } else {
        await submitTemplateDetails({ data }).unwrap();
        addNotification({ message: "Template details submitted successfully!", type: "success" });
      }
    } catch (e) {
      addNotification({
        message: e?.message || "Failed to submit template details.",
        type: "error",
      });
    }
  };

  return (
    <Formik enableReinitialize>
      {() => (
        <Form>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <LoadingButton
              type="button"
              variant="contained"
              color="primary"
              onClick={handleTemplateSubmit}
              disabled={false}
            >
              {mode === "add" ? "Create Template" : "Update Template"}
            </LoadingButton>

            <TextField
              label="Template Name"
              variant="outlined"
              fullWidth
              value={template_name}
              onChange={(e) => setTemplateName(e.target.value)}
            />

            {/* ---------- Fabric Details ---------- */}
            <Accordion>
              <AccordionDetails>
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  >
                    <Grid container className={classes.accordionSummaryTextContainer}>
                      <Grid item xs={4}>Fabric Details</Grid>
                      <Grid item xs={4}>面料细节</Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box className={classes.tableHeader}>
                      <Grid container>
                        <Grid item xs={6}>
                          <Typography>Item</Typography>
                          <Typography>物品</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Fabric Types (multiple)</Typography>
                          <Typography>面料类型（多选）</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {fabricsItems.map((row, index) => (
                      <Box key={index} className={classes.row}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <Autocomplete
                                options={fabricData?.items || []}
                                getOptionLabel={(o) => `${o.name} - ${o.chinese_name || ""}`}
                                isOptionEqualToValue={isEqualById}
                                value={row.item ?? null}
                                onChange={(e, v) => handleChangeFabric(e, "item", index, v)}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Select Item" />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <Autocomplete
                                multiple
                                options={fabricData?.fabricTypes || []}
                                getOptionLabel={(o) => `${o.name} - ${o.chinese_name || ""}`}
                                isOptionEqualToValue={isEqualById}
                                value={row.fabricTypes ?? []}
                                onChange={(e, v) => handleChangeFabric(e, "fabricTypes", index, v)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select one or more Fabric Types"
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Button
                              onClick={() => deleteRowsFabric(index)}
                              disabled={fabricsItems.length === 1}
                              startIcon={<RemoveCircleOutline />}
                              color="error"
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={addNewRowFabric}
                        startIcon={<AddCircleOutline />}
                      >
                        Add New Row
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            </Accordion>

            {/* ---------- Trim Details ---------- */}
            <Accordion>
              <AccordionDetails>
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  >
                    <Grid container className={classes.accordionSummaryTextContainer}>
                      <Grid item xs={4}>Trim Details</Grid>
                      <Grid item xs={4}>修剪细节</Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box className={classes.tableHeader}>
                      <Grid container>
                        <Grid item xs={6}>
                          <Typography>Trim Item</Typography>
                          <Typography>物品</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Trim Type</Typography>
                          <Typography>修剪类型</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {trimItems.map((row, index) => (
                      <Box key={index} className={classes.row} sx={{ mt: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <Autocomplete
                                options={trimData?.items || []}
                                getOptionLabel={(o) => `${o.name} - ${o.chinese_name || ""}`}
                                isOptionEqualToValue={isEqualById}
                                value={row.item ?? null}
                                onChange={(e, v) => handleChangeTrim(e, "item", index, v)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Trim Item"
                                    variant="standard"
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <Autocomplete
                                options={trimTypeOptions}
                                loading={trimTypeLoading}
                                getOptionLabel={(o) =>
                                  `${o?.name ?? ""}${
                                    o?.chinese_name ? ` - ${o.chinese_name}` : ""
                                  }`
                                }
                                isOptionEqualToValue={isEqualById}
                                value={row.trim_type ?? null}
                                onChange={(e, v) => handleChangeTrim(e, "trim_type", index, v)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Trim Type"
                                    variant="standard"
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sx={{ mt: 1, textAlign: "right" }}>
                            <Button
                              onClick={() => deleteRowTrim(index)}
                              disabled={trimItems.length === 1}
                              startIcon={<RemoveCircleOutline />}
                              color="error"
                              variant="outlined"
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={addNewRowTrim}
                        startIcon={<AddCircleOutline />}
                      >
                        Add New Row
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            </Accordion>

            {/* ---------- Label Details (Label Type -> Multiple Labels) ---------- */}
            <Accordion>
              <AccordionDetails>
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  >
                    <Grid container className={classes.accordionSummaryTextContainer}>
                      <Grid item xs={4}>Labels</Grid>
                      <Grid item xs={4}>标签</Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails sx={{ padding: 0 }}>
                    {/* Table header */}
                    <Box className={classes.tableHeader}>
                      <Grid container>
                        <Grid item xs={5} className={classes.cell}>
                          <Box>Label Type</Box>
                          <Box>标签类型</Box>
                        </Grid>
                        <Grid item xs={6} className={classes.cell}>
                          <Box>Labels (Multiple)</Box>
                          <Box>标签（多选）</Box>
                        </Grid>
                        <Grid item xs={1} className={classes.cell}></Grid>
                      </Grid>
                    </Box>

                    {labelItems.map((row, index) => {
                      const { label_type, labels } = row;

                      // Filter labels by selected label type
                      const filteredLabelOptions = label_type?.id
                        ? labelOptions.filter(
                            (opt) =>
                              String(opt.label_type_id) === String(label_type.id)
                          )
                        : labelOptions;

                      return (
                        <Box key={index} className={classes.row} sx={{ marginY: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            {/* Label Type */}
                            <Grid item xs={5}>
                              <FormControl
                                variant="standard"
                                className={classes.tableBodyCell}
                                fullWidth
                              >
                                <Autocomplete
                                  options={labelTypeOptions}
                                  value={label_type}
                                  isOptionEqualToValue={isEqualById}
                                  getOptionLabel={(option) =>
                                    option?.name || ""
                                  }
                                  onChange={(event, value) =>
                                    handleChangeLabelRow(
                                      event,
                                      "label_type",
                                      index,
                                      value
                                    )
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select Label Type"
                                      variant="standard"
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            {/* Multiple Labels */}
                            <Grid item xs={6}>
                              <FormControl
                                variant="standard"
                                className={classes.tableBodyCell}
                                fullWidth
                              >
                                <Autocomplete
                                  multiple
                                  options={filteredLabelOptions}
                                  value={labels}
                                  isOptionEqualToValue={isEqualById}
                                  getOptionLabel={(option) =>
                                    option?.name || ""
                                  }
                                  onChange={(event, value) =>
                                    handleChangeLabelRow(
                                      event,
                                      "labels",
                                      index,
                                      value
                                    )
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select Labels"
                                      variant="standard"
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            {/* Remove button */}
                            <Grid item xs={1}>
                              <Button 
                                onClick={() => deleteRowLabel(index)}
                                disabled={labelItems.length === 1}
                              >
                                <RemoveCircleOutline />
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })}

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={addNewRowLabel}
                        startIcon={<AddCircleOutline />}
                      >
                        Add New Row
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            </Accordion>
          </Box>

          {mode !== "view" && (
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleTemplateSubmit}>
                Save
              </Button>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default ProductionOrderTemplateForm;
